<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { vOnClickOutside } from '@vueuse/components';
import format from 'date-fns/format';
import fromUnixTime from 'date-fns/fromUnixTime';

import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import ConversationApi from 'dashboard/api/inbox/conversation';
import ConversationBox from 'dashboard/components/widgets/conversation/ConversationBox.vue';
import ConversationSidebar from 'dashboard/components/widgets/conversation/ConversationSidebar.vue';
import SidepanelSwitch from 'dashboard/components-next/Conversation/SidepanelSwitch.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CompactDateInboxFilters from '../settings/reports/components/CompactDateInboxFilters.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();

const currentChat = useMapGetter('getSelectedChat');
const conversationById = useMapGetter('getConversationById');

const conversations = ref([]);
const totalCount = ref(0);
const hasTotalCount = ref(false);
const missedSessionsPerConversation = ref({});
const totalMissedSessions = ref(0);
const page = ref(1);
const hasMore = ref(false);
const isLoadingList = ref(false);
const isFetchingMore = ref(false);
const listError = ref('');
const showFilterMenu = ref(false);
const isViewActive = ref(true);
const latestListRequestToken = ref(0);

const RESULTS_PER_PAGE = 25;

const searchInput = ref('');
const searchQuery = ref('');
const sortOrder = ref('last_activity_at_desc');
const archiveFilters = ref({
  inboxId: null,
  userIds: [],
  labelId: null,
  teamId: null,
  vipLevelId: null,
  issueId: null,
  priority: null,
});

let archiveFilterRefreshTimeoutId = null;

const selectedConversationId = computed(() =>
  Number(route.params.conversationId || 0)
);
const reportFrom = computed(() => Number(route.query.from || 0));
const reportTo = computed(() => Number(route.query.to || 0));
const archiveFilter = computed(() => String(route.query.archive_filter || ''));

const hasDateFilter = computed(
  () => reportFrom.value > 0 && reportTo.value > 0
);
const isMissedChatsArchive = computed(
  () => archiveFilter.value === 'missed_chats'
);
const sortLabel = computed(() =>
  sortOrder.value === 'last_activity_at_asc' ? 'Oldest' : 'Newest'
);
const countLabel = computed(() => {
  const count = Number(totalCount.value || 0);
  let suffix = '';
  if (!hasTotalCount.value && hasMore.value) {
    suffix = '+';
  }
  const base = `${count.toLocaleString()}${suffix} chats`;
  if (isMissedChatsArchive.value && totalMissedSessions.value > count) {
    return `${base} (${totalMissedSessions.value} missed sessions)`;
  }
  return base;
});
const showClearSearch = computed(() =>
  Boolean(searchInput.value.trim() || searchQuery.value)
);
const hasConversations = computed(() => conversations.value.length > 0);
const showConversationSidebar = computed(() => {
  if (!currentChat.value?.id) return false;

  const {
    is_contact_sidebar_open: isContactSidebarOpen,
    is_ai_assistant_open: isAiAssistantOpen,
  } = uiSettings.value || {};

  return Boolean(isContactSidebarOpen || isAiAssistantOpen);
});
const dateRangeLabel = computed(() => {
  if (!hasDateFilter.value) {
    return 'All dates';
  }

  return `${format(fromUnixTime(reportFrom.value), 'd MMM yyyy')} - ${format(
    fromUnixTime(reportTo.value),
    'd MMM yyyy'
  )}`;
});

const buildInitials = value => {
  const source = String(value || '').trim();
  if (!source) return '?';

  const words = source.split(/\s+/).filter(Boolean);
  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }

  return words
    .slice(0, 2)
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase();
};

const normalizeMessagePreview = content => {
  const preview = String(content || '')
    .replace(/\s+/g, ' ')
    .trim();
  return preview || 'No message content';
};

const RESOLVED_INACTIVITY_NOTICE_REGEX =
  /^Conversation was (marked )?resolved by .*due to inactivity/i;
const LEGACY_AUTO_RESOLVE_ACTIVITY_REGEX =
  /^\[system\]\s*Auto-resolved due to inactivity/i;

const isIgnoredPreviewMessage = message => {
  if (!message) return true;

  const messageType = Number(message.message_type);
  if (messageType === 2) {
    return true;
  }

  const normalizedContent = String(message.content || '').trim();
  if (!normalizedContent) return true;

  return (
    RESOLVED_INACTIVITY_NOTICE_REGEX.test(normalizedContent) ||
    LEGACY_AUTO_RESOLVE_ACTIVITY_REGEX.test(normalizedContent)
  );
};

const normalizePreviewMessages = messages => {
  const uniqueMessages = [];
  const seen = new Set();

  messages.forEach(message => {
    if (isIgnoredPreviewMessage(message)) return;

    const normalizedId = Number(message?.id || 0);
    const dedupeKey =
      normalizedId > 0
        ? `id:${normalizedId}`
        : `fallback:${message?.message_type}:${message?.created_at}:${message?.content}`;

    if (seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    uniqueMessages.push(message);
  });

  return uniqueMessages.slice(0, 3);
};

const missedSessionCount = conversation => {
  const id = String(conversation?.id || '');
  return Number(missedSessionsPerConversation.value[id] || 0);
};

const conversationDisplayName = conversation => {
  return (
    conversation?.meta?.sender?.name ||
    conversation?.meta?.sender?.phone_number ||
    `#${conversation?.id}`
  );
};

const conversationAssigneeName = conversation => {
  return conversation?.meta?.assignee?.name || 'Unassigned';
};

const conversationDateSource = conversation => {
  const ts = Number(
    conversation?.created_at ||
      conversation?.last_activity_at ||
      conversation?.timestamp ||
      0
  );
  return ts > 0 ? fromUnixTime(ts) : new Date();
};

const conversationDateLabel = conversation => {
  return format(conversationDateSource(conversation), 'dd MMM yyyy');
};

const conversationDateTitle = conversation => {
  return format(
    conversationDateSource(conversation),
    'EEE, d MMM yyyy, hh:mm aa'
  );
};

const conversationPreviewMessages = conversation => {
  const previewMessages = Array.isArray(conversation?.preview_messages)
    ? conversation.preview_messages
    : [];

  const normalizedPreviewMessages = normalizePreviewMessages(previewMessages);
  if (normalizedPreviewMessages.length) {
    return normalizedPreviewMessages;
  }

  const fallbackMessages = [
    conversation?.last_non_activity_message,
    ...(Array.isArray(conversation?.messages) ? conversation.messages : []),
  ].filter(Boolean);

  return normalizePreviewMessages(fallbackMessages);
};

const messageBadge = message => {
  return buildInitials(
    message?.sender?.available_name ||
      message?.sender?.name ||
      message?.sender?.phone_number
  );
};

const messagePreview = message => normalizeMessagePreview(message?.content);

const normalizePositiveId = value => {
  const normalizedId = Number(value || 0);
  return Number.isFinite(normalizedId) && normalizedId > 0
    ? normalizedId
    : null;
};

const normalizeArchiveFilterState = ({
  selectedInbox = null,
  selectedAgents = [],
  selectedLabel = null,
  selectedTeam = null,
  selectedVip = null,
  selectedIssue = null,
  selectedPriority = null,
} = {}) => ({
  inboxId: normalizePositiveId(selectedInbox?.id),
  userIds: Array.isArray(selectedAgents)
    ? selectedAgents
        .map(agent => normalizePositiveId(agent?.id))
        .filter(Boolean)
        .sort((left, right) => left - right)
    : [],
  labelId: normalizePositiveId(selectedLabel?.id),
  teamId: normalizePositiveId(selectedTeam?.id),
  vipLevelId: normalizePositiveId(selectedVip?.id),
  issueId: normalizePositiveId(selectedIssue?.id),
  priority: selectedPriority?.id || null,
});

const areArchiveFiltersEqual = (left, right) => {
  return (
    left.inboxId === right.inboxId &&
    left.labelId === right.labelId &&
    left.teamId === right.teamId &&
    left.vipLevelId === right.vipLevelId &&
    left.issueId === right.issueId &&
    left.priority === right.priority &&
    left.userIds.length === right.userIds.length &&
    left.userIds.every((value, index) => value === right.userIds[index])
  );
};

async function resetAndFetchConversations() {
  if (!isViewActive.value) return;
  page.value = 1;
  hasMore.value = false;
  conversations.value = [];
  totalCount.value = 0;
  hasTotalCount.value = false;
  // eslint-disable-next-line no-use-before-define
  await fetchArchiveConversations();
}

const clearScheduledArchiveRefresh = () => {
  if (!archiveFilterRefreshTimeoutId) return;
  window.clearTimeout(archiveFilterRefreshTimeoutId);
  archiveFilterRefreshTimeoutId = null;
};

const scheduleArchiveRefresh = () => {
  clearScheduledArchiveRefresh();
  archiveFilterRefreshTimeoutId = window.setTimeout(() => {
    archiveFilterRefreshTimeoutId = null;
    if (!isViewActive.value) return;
    resetAndFetchConversations().catch(() => undefined);
  }, 120);
};

const onArchiveFilterChange = payload => {
  const nextFilters = normalizeArchiveFilterState(payload);
  if (areArchiveFiltersEqual(archiveFilters.value, nextFilters)) {
    return;
  }

  archiveFilters.value = nextFilters;
  scheduleArchiveRefresh();
};

const buildRequestParams = pageNumber => ({
  inboxId: archiveFilters.value.inboxId || undefined,
  assigneeType: 'all',
  status: 'resolved',
  page: pageNumber,
  userIds: archiveFilters.value.userIds.length
    ? archiveFilters.value.userIds
    : undefined,
  labelId: archiveFilters.value.labelId || undefined,
  teamId: archiveFilters.value.teamId || undefined,
  vipLevelId: archiveFilters.value.vipLevelId || undefined,
  issueId: archiveFilters.value.issueId || undefined,
  priority: archiveFilters.value.priority || undefined,
  sortBy: sortOrder.value,
  q: searchQuery.value || undefined,
  conversationType: isMissedChatsArchive.value
    ? 'missed_chats_archive'
    : undefined,
  reportFrom: reportFrom.value || undefined,
  reportTo: reportTo.value || undefined,
  previewMessagesCount: 3,
  includeMeta: false,
});

const mergeConversations = incomingConversations => {
  const merged = new Map(
    conversations.value.map(conversation => [conversation.id, conversation])
  );

  incomingConversations.forEach(conversation => {
    merged.set(conversation.id, conversation);
  });

  return Array.from(merged.values());
};

const clearSelectedState = async () => {
  await store.dispatch('clearSelectedState');
};

const ensureContactSidebarOpen = () => {
  if (!currentChat.value?.id) return;

  const {
    is_contact_sidebar_open: isContactSidebarOpen,
    is_ai_assistant_open: isAiAssistantOpen,
  } = uiSettings.value || {};

  if (isContactSidebarOpen || isAiAssistantOpen) return;

  updateUISettings({
    is_contact_sidebar_open: true,
    is_ai_assistant_open: false,
    is_copilot_panel_open: false,
  });
};

const findListedConversationById = conversationId => {
  const targetId = Number(conversationId || 0);
  if (!targetId) return null;

  return (
    conversations.value.find(
      conversation => Number(conversation?.id || 0) === targetId
    ) || null
  );
};

const loadConversation = conversationId => {
  const nextConversationId = Number(conversationId || 0);
  if (!nextConversationId || !isViewActive.value) return;
  if (Number(currentChat.value?.id || 0) === nextConversationId) return;

  // ── Synchronous lookup ──────────────────────────────────────────────────
  // Mirrors ConversationView.setActiveChat(): find the conversation from the
  // Vuex store (pre-populated by fetchArchiveConversations), then dispatch
  // setActiveChat as fire-and-forget.  The first synchronous commit inside
  // setActiveChat (SET_CURRENT_CHAT_WINDOW) switches the UI instantly;
  // message fetching happens silently in the background – identical to the
  // normal conversation page.
  let conversation = conversationById.value(nextConversationId);

  if (!conversation) {
    const listed = findListedConversationById(nextConversationId);
    if (listed) {
      store.commit('UPDATE_CONVERSATION', listed);
      conversation = conversationById.value(nextConversationId) || listed;
    }
  }

  if (!conversation) {
    // Deep-linked URL to a conversation not in our list – fetch from API.
    store
      .dispatch('getConversation', nextConversationId)
      .then(() => {
        if (!isViewActive.value) return;
        const fetched = conversationById.value(nextConversationId);
        if (!fetched) return;
        store.dispatch('setActiveChat', { data: fetched }).then(() => {
          if (isViewActive.value) ensureContactSidebarOpen();
        });
      })
      .catch(() => undefined);
    return;
  }

  if (conversation.id === currentChat.value?.id) return;

  listError.value = '';
  store.dispatch('setActiveChat', { data: conversation }).then(() => {
    if (isViewActive.value) {
      ensureContactSidebarOpen();
      emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
    }
  });
};

const navigateToConversation = async conversationId => {
  if (!isViewActive.value) return;
  const nextConversationId = Number(conversationId || 0);
  if (!nextConversationId) return;

  if (nextConversationId === selectedConversationId.value) {
    loadConversation(nextConversationId);
    return;
  }

  if (!isViewActive.value) return;
  await router.replace({
    name: 'archives_view',
    params: {
      accountId: route.params.accountId,
      conversationId: nextConversationId,
    },
    query: route.query,
  });
};

const syncSelectionWithRoute = async () => {
  if (!isViewActive.value) return;
  if (!conversations.value.length) {
    await clearSelectedState();
    if (!isViewActive.value) return;

    if (selectedConversationId.value) {
      if (!isViewActive.value) return;
      await router.replace({
        name: 'archives_view',
        params: { accountId: route.params.accountId },
        query: route.query,
      });
    }
    return;
  }

  const matchingConversation = conversations.value.find(
    conversation => Number(conversation.id) === selectedConversationId.value
  );

  if (!matchingConversation) {
    const fallbackConversationId = Number(conversations.value[0]?.id || 0);
    if (!fallbackConversationId) return;

    if (!isViewActive.value) return;
    await router.replace({
      name: 'archives_view',
      params: {
        accountId: route.params.accountId,
        conversationId: fallbackConversationId,
      },
      query: route.query,
    });
    loadConversation(fallbackConversationId);
    return;
  }

  loadConversation(matchingConversation.id);
};

const fetchArchiveConversations = async ({ append = false } = {}) => {
  if (!isViewActive.value) return;
  latestListRequestToken.value += 1;
  const requestToken = latestListRequestToken.value;

  if (append) {
    if (isFetchingMore.value || isLoadingList.value || !hasMore.value) return;
    isFetchingMore.value = true;
  } else {
    isLoadingList.value = true;
    listError.value = '';
    isFetchingMore.value = false;
  }

  const nextPage = append ? page.value + 1 : 1;

  try {
    const response = await ConversationApi.get(buildRequestParams(nextPage));
    if (!isViewActive.value || requestToken !== latestListRequestToken.value)
      return;

    const payload = response?.data?.data?.payload || [];
    const meta = response?.data?.data?.meta || {};

    conversations.value = append ? mergeConversations(payload) : payload;

    // Pre-push every fetched conversation into the Vuex store so that
    // loadConversation can find them synchronously (same approach the
    // normal ChatList / ConversationView uses).
    payload.forEach(c => {
      if (!conversationById.value(Number(c.id))) {
        store.commit('UPDATE_CONVERSATION', c);
      }
    });

    page.value = nextPage;
    if (meta.missed_sessions_per_conversation) {
      missedSessionsPerConversation.value = meta.missed_sessions_per_conversation;
      totalMissedSessions.value = Number(meta.total_missed_sessions || 0);
    }
    const allCount = Number(meta.all_count || 0);
    const hasCountFromResponse = Number.isFinite(allCount) && allCount > 0;

    if (hasCountFromResponse) {
      totalCount.value = allCount;
      hasTotalCount.value = true;
    } else if (!hasTotalCount.value) {
      totalCount.value = conversations.value.length || 0;
    }

    hasMore.value = hasTotalCount.value
      ? conversations.value.length < totalCount.value && payload.length > 0
      : payload.length >= RESULTS_PER_PAGE;

    if (!append) {
      syncSelectionWithRoute().catch(() => undefined);
    }
  } catch {
    if (!isViewActive.value || requestToken !== latestListRequestToken.value)
      return;
    if (!append) {
      conversations.value = [];
      totalCount.value = 0;
      hasTotalCount.value = false;
      hasMore.value = false;
      listError.value = 'Failed to load archives.';
      await clearSelectedState();
    }
  } finally {
    if (isViewActive.value && requestToken === latestListRequestToken.value) {
      isLoadingList.value = false;
      isFetchingMore.value = false;
    }
  }
};

const applySearch = async () => {
  clearScheduledArchiveRefresh();
  searchQuery.value = searchInput.value.trim();
  await resetAndFetchConversations();
};

const clearSearch = async () => {
  if (!showClearSearch.value) return;
  clearScheduledArchiveRefresh();
  searchInput.value = '';
  searchQuery.value = '';
  await resetAndFetchConversations();
};

const toggleSortOrder = async () => {
  sortOrder.value =
    sortOrder.value === 'last_activity_at_desc'
      ? 'last_activity_at_asc'
      : 'last_activity_at_desc';
};

const replaceRouteQuery = async queryPatch => {
  const nextQuery = {
    ...route.query,
    ...queryPatch,
  };

  Object.keys(nextQuery).forEach(key => {
    if (
      nextQuery[key] === undefined ||
      nextQuery[key] === null ||
      nextQuery[key] === ''
    ) {
      delete nextQuery[key];
    }
  });

  await router.replace({
    name: 'archives_view',
    params: selectedConversationId.value
      ? {
          accountId: route.params.accountId,
          conversationId: selectedConversationId.value,
        }
      : { accountId: route.params.accountId },
    query: nextQuery,
  });
};

const clearDateFilter = async () => {
  await replaceRouteQuery({ from: undefined, to: undefined });
};

const setArchiveFilter = async value => {
  await replaceRouteQuery({
    archive_filter: value === 'missed_chats' ? 'missed_chats' : undefined,
  });
  showFilterMenu.value = false;
};

const clearArchiveFilter = async () => {
  await replaceRouteQuery({ archive_filter: undefined });
};

const handleListScroll = async event => {
  const element = event?.target;
  if (!element || !hasMore.value || isFetchingMore.value) return;

  const distanceToBottom =
    element.scrollHeight - element.scrollTop - element.clientHeight;

  if (distanceToBottom < 120) {
    await fetchArchiveConversations({ append: true });
  }
};

watch(
  () => [
    route.query.from,
    route.query.to,
    route.query.archive_filter,
    sortOrder.value,
  ],
  async () => {
    if (!isViewActive.value) return;
    clearScheduledArchiveRefresh();
    await resetAndFetchConversations();
  }
);

watch(selectedConversationId, async conversationId => {
  if (!isViewActive.value) return;
  if (!conversations.value.length) return;

  if (!conversationId) {
    await syncSelectionWithRoute();
    return;
  }

  loadConversation(conversationId);
});

const goBackToMissedChats = async () => {
  await router.push({
    name: 'missed_chats_reports',
    params: { accountId: route.params.accountId },
  });
};

onMounted(async () => {
  store.commit('CLEAR_LIST_LOADING_STATUS');
  searchInput.value = String(route.query.q || '');
  searchQuery.value = String(route.query.q || '');
  sortOrder.value = String(route.query.sort_by || 'last_activity_at_desc');
  await resetAndFetchConversations();
});

const markViewInactive = () => {
  isViewActive.value = false;
  clearScheduledArchiveRefresh();
  latestListRequestToken.value += 1;
};

onBeforeUnmount(() => {
  markViewInactive();
  clearSelectedState().catch(() => undefined);
});
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <section class="archives-layout conversation-page-theme">
    <div class="css-og8kck archives-sidebar-shell">
      <div class="css-sn4l67">
        <div class="css-sn4l67">
          <div class="css-1py3ffp archives-sidebar-panel">
            <div class="css-116lk7b">
              <div class="css-e76p5t">
                <div class="css-111rffh">Archives</div>
                <div class="css-kr8fjv">
                  <div class="css-mc000a archives-filter-anchor">
                    <button
                      class="archives-icon-button"
                      type="button"
                      @click="showFilterMenu = !showFilterMenu"
                    >
                      <span class="archives-icon-wrap">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          width="20"
                          height="20"
                        >
                          <path
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M10 12h10m-10 0a2 2 0 0 1-4 0m4 0a2 2 0 0 0-4 0m0 0H4m11 6a2 2 0 0 0 4 0m-4 0a2 2 0 0 1 4 0m-4 0H4m15 0h1M12 6a2 2 0 1 0 4 0m-4 0a2 2 0 1 1 4 0m-4 0H4m12 0h4"
                          />
                        </svg>
                      </span>
                    </button>

                    <div
                      v-if="showFilterMenu"
                      v-on-click-outside="() => (showFilterMenu = false)"
                      class="archives-filter-menu"
                    >
                      <button
                        class="archives-filter-menu__item"
                        :class="{ 'is-selected': isMissedChatsArchive }"
                        type="button"
                        @click="setArchiveFilter('missed_chats')"
                      >
                        Missed chats
                      </button>
                      <button
                        class="archives-filter-menu__item"
                        :class="{ 'is-selected': !isMissedChatsArchive }"
                        type="button"
                        @click="setArchiveFilter(undefined)"
                      >
                        All archived chats
                      </button>
                      <button
                        v-if="hasDateFilter"
                        class="archives-filter-menu__item"
                        type="button"
                        @click="clearDateFilter"
                      >
                        Clear date filter
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="isMissedChatsArchive" class="archives-back-row">
              <button
                class="archives-back-button"
                type="button"
                @click="goBackToMissedChats"
              >
                <span class="archives-back-button__icon" aria-hidden="true">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                  >
                    <path
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="m15 19-7-7 7-7"
                    />
                  </svg>
                </span>
                <span>Back to Missed Chats</span>
              </button>
            </div>

            <div class="css-1j8yl8o">
              <div class="css-1b0jxoo">
                <div class="css-eox8i6">
                  <div class="css-a3l5mu">
                    <input
                      v-model="searchInput"
                      class="css-sv5c8s"
                      placeholder="Search in archives..."
                      @keyup.enter="applySearch"
                    />
                    <div class="css-oayotw">
                      <div class="css-1hw56h8" />
                    </div>
                    <button
                      class="css-1jkeysg"
                      :disabled="!searchInput.trim()"
                      title="Search"
                      @click="applySearch"
                    >
                      <span class="archives-search-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          width="16"
                          height="16"
                        >
                          <path
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M3 10a7 7 0 1 0 14 0 7 7 0 0 0-14 0M21 21l-6-6"
                          />
                        </svg>
                      </span>
                    </button>
                    <button
                      class="css-z9j85r"
                      :class="{ 'is-visible': showClearSearch }"
                      title="Clear"
                      @click="clearSearch"
                    >
                      <span class="archives-clear-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          width="16"
                          height="16"
                        >
                          <path
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="m6 6 12 12m0-12L6 18"
                          />
                        </svg>
                      </span>
                    </button>
                  </div>

                  <div class="css-h23tv4">
                    <CompactDateInboxFilters
                      class="archives-add-filter-toolbar"
                      :show-date-filter="false"
                      :emit-initial-change="false"
                      show-agents
                      show-inbox
                      show-labels
                      show-teams
                      show-vip
                      show-issue
                      show-priority
                      hide-inactive-filters
                      @filter-change="onArchiveFilterChange"
                    />

                    <div v-if="hasDateFilter" class="archives-chip">
                      <div class="archives-chip__content">
                        <span class="archives-chip__icon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            width="20"
                            height="20"
                          >
                            <path
                              stroke="currentColor"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M4 11h16M8 3v4m8-4v4m-8 8h2v2H8zM4 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z"
                            />
                          </svg>
                        </span>
                        <div class="css-1rdflu1">
                          <span>
                            <strong>Date</strong>
                            <span>
                              <strong> is </strong>{{ dateRangeLabel }}
                            </span>
                          </span>
                        </div>
                      </div>
                      <button
                        class="archives-chip__remove"
                        type="button"
                        title="Remove"
                        @click="clearDateFilter"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          width="20"
                          height="20"
                        >
                          <path
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="m6 6 12 12m0-12L6 18"
                          />
                        </svg>
                      </button>
                    </div>

                    <div v-if="isMissedChatsArchive" class="archives-chip">
                      <div class="archives-chip__content">
                        <span class="archives-chip__icon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            width="20"
                            height="20"
                          >
                            <path
                              stroke="currentColor"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="m21 6.99-7 9.5c-.077.115-.304.404-.432.457a.7.7 0 0 1-.763-.152L3 6.99m0 0h5m-5 0v5"
                            />
                          </svg>
                        </span>
                        <div class="css-o2hzyu">Missed chats</div>
                      </div>
                      <button
                        class="archives-chip__remove"
                        type="button"
                        title="Remove"
                        @click="clearArchiveFilter"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          width="20"
                          height="20"
                        >
                          <path
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="m6 6 12 12m0-12L6 18"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div class="css-1mwn02k">
                    <div class="css-ov1ktg">
                      <div>
                        <span>{{ countLabel }}</span>
                      </div>
                    </div>
                    <div class="css-1u525pn">
                      <button
                        class="archives-sort-button"
                        type="button"
                        @click="toggleSortOrder"
                      >
                        <span>{{ sortLabel }}</span>
                        <span class="archives-sort-button__icon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            width="20"
                            height="20"
                          >
                            <path
                              stroke="currentColor"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="m6 9 6 6 6-6"
                            />
                          </svg>
                        </span>
                      </button>
                    </div>
                  </div>

                  <div v-if="listError" class="archives-inline-error">
                    {{ listError }}
                  </div>
                </div>

                <ul class="css-1yt0wlr" @scroll.passive="handleListScroll">
                  <li
                    v-for="conversation in conversations"
                    :id="`archive-item-${conversation.id}`"
                    :key="conversation.id"
                    class="privacy-masker"
                    :class="
                      Number(conversation.id) === selectedConversationId
                        ? 'css-162royd'
                        : 'css-1wkoiq1'
                    "
                  >
                    <div
                      class="css-1i13yu"
                      @click="navigateToConversation(conversation.id)"
                    >
                      <div class="css-1ibr01v">
                        <div class="css-1iy6hkk">
                          <div class="css-1sg2lsz">
                            <p class="css-1yz5qyy archives-item-title">
                              {{ conversationDisplayName(conversation) }}
                            </p>
                          </div>
                          <p class="css-7mbya9">
                            <span>Agent: </span>
                            <span>{{
                              conversationAssigneeName(conversation)
                            }}</span>
                          </p>
                          <p
                            v-if="missedSessionCount(conversation) > 0"
                            class="missed-sessions-badge"
                          >
                            Missed {{ missedSessionCount(conversation) }}
                            {{
                              missedSessionCount(conversation) === 1
                                ? 'time'
                                : 'times'
                            }}
                          </p>
                        </div>
                        <div
                          class="css-n3y541"
                          :title="conversationDateTitle(conversation)"
                        >
                          <span>{{ conversationDateLabel(conversation) }}</span>
                        </div>
                      </div>

                      <div class="css-1sg2lsz">
                        <div class="css-nzt1xy">
                          <div
                            v-for="message in conversationPreviewMessages(
                              conversation
                            )"
                            :key="`${conversation.id}-${message.id}`"
                            class="css-wh4h37"
                          >
                            <span class="css-1ed8kz2">
                              {{ messageBadge(message) }}:
                            </span>
                            <span>
                              <span>{{ messagePreview(message) }}</span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>

                  <li
                    v-if="!isLoadingList && !hasConversations"
                    class="archives-empty-list"
                  >
                    No archived chats found for this filter.
                  </li>

                  <li
                    v-if="isLoadingList || isFetchingMore"
                    class="archives-loading-row"
                  >
                    <Spinner class="text-n-slate-11" />
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="css-v8ql05 archives-main-panel">
      <ConversationBox
        class="flex-1 [&.conversation-details-wrap]:!border-0"
        :inbox-id="0"
        :is-on-expanded-layout="false"
        hide-reply-box
      >
        <SidepanelSwitch v-if="currentChat.id" />
      </ConversationBox>
    </div>

    <ConversationSidebar
      v-if="showConversationSidebar"
      :current-chat="currentChat"
    />
  </section>
</template>

<style scoped>
.missed-sessions-badge {
  display: inline-block;
  margin-top: 2px;
  padding: 1px 6px;
  font-size: 11px;
  font-weight: 600;
  color: #b91c1c;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 4px;
  line-height: 1.4;
}

.archives-layout {
  display: flex;
  width: 100%;
  height: 100%;
  min-width: 0;
  background-color: var(--wlab-bg-sidebar);
}

.archives-back-row {
  padding: 12px 12px 0 12px;
}

.archives-back-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid var(--border-basic-tertiary, #e5e7eb);
  background: #ffffff;
  color: #111827;
  height: 34px;
  padding: 0 10px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.archives-back-button:hover {
  background: #f9fafb;
}

.archives-back-button__icon {
  display: inline-flex;
  align-items: center;
  color: currentColor;
}

.archives-sidebar-shell {
  background: var(--surface-primary-default, #ffffff);
  overflow: hidden;
}

.archives-sidebar-panel {
  display: flex;
  flex-direction: column;
}

.archives-main-panel {
  box-sizing: border-box;
  height: 100%;
  min-width: 320px;
  flex: 2 1 50%;
  border-left: 1px solid var(--border-basic-tertiary, #eeeeef);
  display: flex;
  overflow: hidden;
}

.archives-filter-anchor {
  position: relative;
}

.archives-filter-menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 200px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: rgba(0, 0, 0, 0.12) 0 14px 38px;
  padding: 6px;
  z-index: 40;
}

.archives-filter-menu__item {
  width: 100%;
  text-align: left;
  padding: 8px 10px;
  border: none;
  background: transparent;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  cursor: pointer;
}

.archives-filter-menu__item:hover {
  background: #f3f4f6;
}

.archives-filter-menu__item.is-selected {
  background: #eef2ff;
  color: #1d4ed8;
}

.archives-icon-button {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
  padding: 4px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: var(--content-basic-primary, #111827);
  cursor: pointer;
}

.archives-icon-button:hover,
.archives-sort-button:hover {
  background: #f3f4f6;
}

.archives-icon-wrap,
.archives-search-icon,
.archives-clear-icon,
.archives-secondary-button__icon,
.archives-sort-button__icon {
  display: flex;
  align-items: center;
}

.archives-secondary-button {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 32px;
  height: 32px;
  padding: 6px 16px 6px 12px;
  border: 1px solid var(--border-basic-primary, #d1d5db);
  border-radius: 8px;
  background: #ffffff;
  color: #111827;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.archives-secondary-button:hover {
  background: #f9fafb;
}

.archives-add-filter-toolbar {
  flex: 1 1 auto;
  min-width: 0;
  padding: 0;
}

.archives-add-filter-toolbar:deep(.cdf-row) {
  justify-content: flex-start;
}

.archives-add-filter-toolbar:deep(.cdf-left) {
  gap: 4px;
}

.archives-add-filter-toolbar:deep(.cdf-secondary-btn),
.archives-add-filter-toolbar:deep(.cdf-chip) {
  height: 32px;
}

.archives-chip {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  border-radius: 8px;
  border: 1px solid var(--border-basic-primary, #d1d5db);
  background-color: var(--surface-tertiary-default, #e6e6e7);
  color: var(--content-basic-primary, #111827);
  overflow: hidden;
  min-height: 32px;
  max-width: 100%;
}

.archives-chip__content {
  display: inline-flex;
  align-items: center;
  overflow: hidden;
  padding: 0 8px;
}

.archives-chip__icon {
  display: flex;
  align-items: center;
}

.archives-chip__remove {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 4px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--content-basic-secondary, #6b7280);
  cursor: pointer;
}

.archives-sort-button {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 32px;
  height: 32px;
  padding: 6px 12px 6px 16px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: var(--content-basic-primary, #111827);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.archives-inline-error {
  margin-top: 8px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
}

.archives-item-title {
  margin: 0;
  line-height: 21px;
  letter-spacing: 0;
  font-size: 14px;
  font-weight: 600;
  font-style: normal;
}

.archives-empty-list,
.archives-loading-row {
  padding: 20px 16px;
  text-align: center;
  color: #6b7280;
  font-size: 13px;
}

.archives-loading-row {
  display: flex;
  justify-content: center;
}

.css-og8kck {
  box-sizing: border-box;
  height: 100%;
  border-left: none;
  min-width: 320px;
  flex: 1 1 25%;
  transition: margin-left var(--transition-duration-fast-2, 0.2s)
    var(--transition-timing-ease-out, ease-out);
  will-change: margin-left;
  margin-left: 0;
}

.css-v8ql05 {
  box-sizing: border-box;
  height: 100%;
  min-width: 320px;
  flex: 2 1 50%;
  transition: margin-left var(--transition-duration-fast-2, 0.2s)
    var(--transition-timing-ease-out, ease-out);
  will-change: margin-left;
  margin-left: 0;
}

.css-sn4l67 {
  width: 100%;
  height: 100%;
  display: block;
}

.css-1py3ffp {
  position: relative;
  box-sizing: border-box;
  height: 100%;
  background-color: var(--surface-primary-default, #ffffff);
  color: var(--content-basic-primary, #111827);
  border-color: var(--border-basic-tertiary, #eeeeef);
}

.css-116lk7b {
  display: contents;
  height: 56px;
}

.css-e76p5t {
  display: flex;
  justify-content: space-between;
  width: 100%;
  height: 56px;
  position: relative;
  text-align: center;
  box-sizing: border-box;
  border-bottom: 1px solid var(--border-basic-tertiary, #eeeeef);
  background-color: var(--surface-primary-default, #ffffff);
}

.css-111rffh {
  color: var(--content-basic-primary, #111827);
  height: 56px;
  line-height: 56px;
  white-space: nowrap;
  box-sizing: border-box;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: 40px;
  margin-left: 0;
  width: calc(100% - 40px);
  text-align: left;
  padding-left: 16px;
  letter-spacing: 0;
  font-size: 18px;
  font-weight: 600;
  font-style: normal;
}

.css-kr8fjv {
  flex-shrink: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.css-mc000a {
  padding: 8px 16px 8px 8px;
}

.css-1j8yl8o {
  height: calc(100% - 53px);
  overflow: hidden;
  z-index: 0;
  position: static;
}

.css-1b0jxoo {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: var(--surface-primary-default, #ffffff);
}

.css-eox8i6 {
  padding: 10px 12px;
  box-sizing: border-box;
  font-size: 12px;
  line-height: 15px;
  color: rgba(66, 77, 87, 0.8);
  width: 100%;
  box-shadow: rgba(0, 0, 0, 0.3) 0 3px 10px -6px;
}

.css-a3l5mu {
  position: relative;
  text-align: right;
  margin-bottom: 10px;
}

.css-sv5c8s {
  display: inline-block;
  width: 100%;
  height: 40px;
  padding: 0 36px;
  font-size: 13px;
  color: rgb(66, 77, 87);
  text-align: left;
  border-radius: 4px;
  border: 1px solid rgb(221, 226, 230);
  background-color: rgb(255, 255, 255);
  box-sizing: border-box;
  transition: border 0.2s ease-in-out;
}

.css-oayotw {
  width: 15px;
  height: 15px;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 13px;
}

.css-1hw56h8 {
  width: 100%;
  height: 100%;
  position: relative;
  visibility: hidden;
  opacity: 0;
  z-index: -1;
  border-radius: 50%;
  border-width: 2px;
  border-color: rgb(74, 144, 226) rgba(74, 144, 226, 0.5)
    rgba(74, 144, 226, 0.5);
  transition: 0.2s ease-in-out;
}

.css-1jkeysg,
.css-z9j85r {
  position: absolute;
  top: 0;
  height: 40px;
  padding: 0 13px;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: 4px;
}

.css-1jkeysg {
  left: 0;
}

.css-z9j85r {
  right: 0;
  visibility: hidden;
  opacity: 0;
  z-index: -1;
  transition: 0.2s ease-in-out;
}

.css-z9j85r.is-visible {
  visibility: visible;
  opacity: 1;
  z-index: 1;
}

.css-h23tv4 {
  width: 100%;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  flex: 1 1 auto;
  box-sizing: border-box;
  gap: 4px;
}

.css-1rdflu1,
.css-o2hzyu {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.css-1rdflu1 {
  max-width: 190px;
}

.css-o2hzyu {
  max-width: 270px;
}

.css-1mwn02k {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
}

.css-ov1ktg {
  display: flex;
}

.css-1u525pn {
  margin-left: auto;
  display: flex;
}

.css-1yt0wlr {
  list-style: none;
  overflow-y: auto;
  flex: 1 1 0%;
  box-sizing: border-box;
  display: block;
  position: relative;
  margin: 0;
  padding: 0;
}

.css-162royd,
.css-1wkoiq1 {
  width: 100%;
  padding: 0 12px;
  box-sizing: border-box;
  position: relative;
}

.css-162royd {
  background-color: var(--surface-primary-active, #eeeeef);
}

.css-162royd::before,
.css-1wkoiq1::before {
  background-color: rgb(67, 132, 245);
  width: 5px;
  height: 100%;
  top: 0;
  left: 0;
  position: absolute;
  content: '';
  opacity: 0;
  visibility: hidden;
}

.css-162royd::before {
  opacity: 1;
  visibility: visible;
}

.css-1i13yu {
  padding: 10px 6px;
  width: 100%;
  box-sizing: border-box;
  border-bottom: 1px solid rgb(221, 226, 230);
  font-size: 14px;
  cursor: pointer;
  color: rgb(66, 77, 87);
}

.css-1ibr01v {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  align-items: flex-start;
}

.css-1iy6hkk {
  line-height: 20px;
  flex: 1 1 0%;
  overflow: hidden;
}

.css-1iy6hkk > * {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.css-1sg2lsz {
  display: flex;
  align-items: center;
}

.css-1yz5qyy {
  flex: 1 1 0%;
  text-overflow: ellipsis;
  overflow: hidden;
}

.css-7mbya9 {
  font-size: 13px;
  height: 20px;
  color: rgba(66, 77, 87, 0.6);
  margin: 0;
}

.css-n3y541 {
  color: rgba(66, 77, 87, 0.6);
  font-size: 12px;
  margin-left: 10px;
  line-height: 20px;
  white-space: nowrap;
}

.css-nzt1xy {
  display: flex;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  line-height: 20px;
  height: 20px;
}

.css-wh4h37 {
  display: flex;
  margin-right: 4px;
  min-width: 0;
}

.css-1ed8kz2 {
  color: var(--content-basic-disabled, #8d8d95);
  margin-right: 4px;
  flex-shrink: 0;
}

@media (max-width: 768px) {
  .archives-layout {
    flex-direction: column;
  }

  .css-og8kck,
  .css-v8ql05 {
    min-width: 0;
    flex: 1 1 auto;
    width: 100%;
  }

  .archives-main-panel {
    border-left: none;
    border-top: 1px solid var(--border-basic-tertiary, #eeeeef);
  }
}
</style>
